import React, { ReactElement, useState } from 'react'
import { useForm } from '@tanstack/react-form'
import * as z from 'zod'

import { useAppDispatch } from 'src/store/hooks'
import {
  Alert,
  Autocomplete,
  Box,
  IconButton,
  Skeleton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  Course,
  Event,
  Objective,
  Reference,
  Syllabus,
} from 'src/v3/core/types'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import AddIcon from '@mui/icons-material/Add'
import { AsyncThunk } from '@reduxjs/toolkit'
import { Status } from 'src/store/store'
import { DeleteIcon } from '../core/icons'

interface FormFields {
  text?: string
  practiceGap?: string
  practiceGapEvidence?: string
  learnerEngagement?: string
  learningOutcomes?: string
  objectives: Partial<Objective>[]
  references: Partial<Reference>[]
}

type Content =
  { kind: 'course'; course: Course } | { kind: 'event'; event: Event }

export let SyllabusBuilder = ({
  content,
  referenceLib,
  syllabus,
  fetchSyllabus,
  loadingStatuses,
  SubmitButton,
  onSuccess,
}: {
  content: Content
  referenceLib: Reference[]
  syllabus?: Syllabus
  fetchSyllabus: AsyncThunk<Syllabus, string, object>
  loadingStatuses: Status[]
  SubmitButton: (props: { loading: boolean }) => ReactElement
  onSuccess?: () => void
}): ReactElement => {
  let dispatch = useAppDispatch()

  let [creating, setCreating] = useState(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let loading = loadingStatuses.some(
    (status) => !status || status === 'uninitialized',
  )

  let formValues: FormFields = {
    text: syllabus?.text || '',
    practiceGap: syllabus?.practiceGap || '',
    practiceGapEvidence: syllabus?.practiceGapEvidence || '',
    learnerEngagement: syllabus?.learnerEngagement || '',
    learningOutcomes: syllabus?.learningOutcomes || '',
    objectives: syllabus?.objectives || [],
    references: syllabus?.references || [],
  }

  let validationSubSchema = z.object({
    id: z.string().optional(),
    text: z.string().nonempty('Text is required.'),
  })

  let validationSchema = z.object({
    text: z.string(),
    practiceGap: z.string(),
    practiceGapEvidence: z.string(),
    learnerEngagement: z.string(),
    learningOutcomes: z.string(),
    objectives: z.array(validationSubSchema),
    references: z.array(validationSubSchema),
  })

  let form = useForm({
    defaultValues: formValues,
    validators: {
      onChange: validationSchema,
    },
    onSubmit: async ({ value }) => {
      setCreating(true)
      let body = {
        text: value.text,
        practiceGap: value.practiceGap,
        practiceGapEvidence: value.practiceGapEvidence,
        learnerEngagement: value.learnerEngagement,
        learningOutcomes: value.learningOutcomes,
        objectives: value.objectives,
        references: value.references,
      }
      let request = !syllabus
        ? axios.post<Syllabus>(`/api/${content.kind}/syllabus`, {
            ...(content.kind === 'course'
              ? { courseID: content.course.id }
              : { eventID: content.event.id }),
            ...body,
          })
        : axios.put<Syllabus>(`/api/${content.kind}/syllabus`, {
            id: syllabus.id,
            ...body,
          })
      request
        .then(pluckData)
        .then(() =>
          dispatch(
            fetchSyllabus(
              content.kind === 'course' ? content.course.id : content.event.id,
            ),
          ),
        )
        .then(() => form.reset())
        .then(() => (onSuccess ? onSuccess() : Promise.resolve()))
        .catch((err) => {
          setErrorMessage(err?.message || 'Error Creating Title Screen')
        })
        .finally(() => {
          setCreating(false)
        })
    },
  })

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {loading ? (
            <Skeleton height={102} />
          ) : (
            <form.Field
              name="text"
              children={(field) => (
                <TextField
                  sx={{ bgcolor: 'background.paperDark' }}
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  value={field.state.value}
                  label="Description"
                  name={field.name}
                  placeholder={
                    content.kind === 'event'
                      ? content.event.description || undefined
                      : undefined
                  }
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta) &&
                    getZodValidationError(field.state.meta.errors)
                  }
                />
              )}
            />
          )}
          {loading ? (
            <Skeleton height={79} />
          ) : (
            <form.Field
              name="practiceGap"
              children={(field) => (
                <TextField
                  sx={{ bgcolor: 'background.paperDark' }}
                  fullWidth
                  multiline
                  minRows={2}
                  variant="outlined"
                  value={field.state.value}
                  label="Practice Gap"
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta) &&
                    getZodValidationError(field.state.meta.errors)
                  }
                />
              )}
            />
          )}
          {loading ? (
            <Skeleton height={79} />
          ) : (
            <form.Field
              name="practiceGapEvidence"
              children={(field) => (
                <TextField
                  sx={{ bgcolor: 'background.paperDark' }}
                  fullWidth
                  multiline
                  minRows={2}
                  variant="outlined"
                  value={field.state.value}
                  label="Practice Gap Evidence"
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta) &&
                    getZodValidationError(field.state.meta.errors)
                  }
                />
              )}
            />
          )}
          {loading ? (
            <Skeleton height={79} />
          ) : (
            <form.Field
              name="learnerEngagement"
              children={(field) => (
                <TextField
                  sx={{ bgcolor: 'background.paperDark' }}
                  fullWidth
                  multiline
                  minRows={2}
                  variant="outlined"
                  value={field.state.value}
                  label="Learner Engagement"
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta) &&
                    getZodValidationError(field.state.meta.errors)
                  }
                />
              )}
            />
          )}
          {loading ? (
            <Skeleton height={79} />
          ) : (
            <form.Field
              name="learningOutcomes"
              children={(field) => (
                <TextField
                  sx={{ bgcolor: 'background.paperDark' }}
                  fullWidth
                  multiline
                  minRows={2}
                  variant="outlined"
                  value={field.state.value}
                  label="Learning Outcomes"
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta) &&
                    getZodValidationError(field.state.meta.errors)
                  }
                />
              )}
            />
          )}
          <Box>
            <form.Field name="objectives" mode="array">
              {(field) => (
                <Box>
                  <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6">Objectives</Typography>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => field.pushValue({ text: '' })}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  {loading && <Skeleton height={24} />}
                  {!field.state.value.length && !loading && (
                    <Typography color="textSecondary">
                      This {content.kind} does not have any objectives yet.
                    </Typography>
                  )}
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    {field.state.value.map((_, i) => (
                      <form.Field key={i} name={`objectives[${i}].text`}>
                        {(subField) => (
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              alignItems: 'center',
                            }}
                          >
                            <TextField
                              sx={{ bgcolor: 'background.paperDark' }}
                              fullWidth
                              variant="outlined"
                              label={`Objective ${i + 1}`}
                              value={subField.state.value}
                              name={`objective-${i + 1}`}
                              onChange={(e) =>
                                subField.handleChange(e.target.value)
                              }
                              onBlur={subField.handleBlur}
                              error={shouldShowZodFieldError(
                                subField.state.meta,
                              )}
                              helperText={
                                shouldShowZodFieldError(subField.state.meta) &&
                                getZodValidationError(
                                  subField.state.meta.errors,
                                )
                              }
                            />
                            <span>
                              <IconButton onClick={() => field.removeValue(i)}>
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Box>
                        )}
                      </form.Field>
                    ))}
                  </Box>
                </Box>
              )}
            </form.Field>
          </Box>
          <Box>
            <form.Field name="references" mode="array">
              {(field) => (
                <Box>
                  <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6">References</Typography>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => field.pushValue({ text: '' })}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  {loading && <Skeleton height={24} />}
                  {!field.state.value.length && !loading && (
                    <Typography color="textSecondary">
                      This {content.kind} does not have any references yet.
                    </Typography>
                  )}
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    {field.state.value.map((_, i) => (
                      <form.Field key={i} name={`references[${i}].text`}>
                        {(subField) => (
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              alignItems: 'center',
                            }}
                          >
                            <Autocomplete
                              fullWidth
                              freeSolo
                              options={referenceLib.filter((reference) =>
                                field.state.value.every(
                                  (r) => r.id !== reference.id,
                                ),
                              )}
                              value={subField.state.value}
                              renderInput={(params) => (
                                <TextField
                                  sx={{ bgcolor: 'background.paperDark' }}
                                  {...params}
                                  label={`Reference ${i + 1}`}
                                  onBlur={subField.handleBlur}
                                  error={shouldShowZodFieldError(
                                    subField.state.meta,
                                  )}
                                  helperText={
                                    shouldShowZodFieldError(
                                      subField.state.meta,
                                    ) &&
                                    getZodValidationError(
                                      subField.state.meta.errors,
                                    )
                                  }
                                />
                              )}
                              renderOption={(props, option) => {
                                // eslint-disable-next-line fp/no-rest-parameters
                                let { key, ...optionProps } = props
                                return (
                                  <li key={key} {...optionProps}>
                                    {option.text}
                                  </li>
                                )
                              }}
                              getOptionLabel={(option) =>
                                typeof option === 'string'
                                  ? option
                                  : option.text || ''
                              }
                              onInputChange={(e, value) => {
                                let existing = referenceLib.find(
                                  (reference) => reference.text === value,
                                )
                                field.handleChange(
                                  field.state.value.map((reference, idx) =>
                                    idx === i
                                      ? existing || { text: value }
                                      : reference,
                                  ),
                                )
                              }}
                            />
                            <span>
                              <IconButton onClick={() => field.removeValue(i)}>
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Box>
                        )}
                      </form.Field>
                    ))}
                  </Box>
                </Box>
              )}
            </form.Field>
          </Box>
          {<SubmitButton loading={creating} />}
        </Box>
      </form>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
